/**
 * ShellBattlefield - the battlefield panel, with the props the shell computes for it.
 *
 * Everything the map is handed comes in as one `map` record and is passed
 * straight through; this adds only what is derived at the call: the active
 * room's id, the REPLACE pick (only for someone who may see the rooms), the
 * team stamp and the redeploy rule's writer, the target's movement, the
 * armed weapon's reach, and the table's movement rulings. Extracted from
 * DeathWatchSimulator; the props are unchanged, but steadied: the map redraws
 * only when something it is handed has changed (the perf overhaul).
 */
import { memo } from 'react';
import * as multiplayer from '../../multiplayer';
import { warnIfRejected } from '../../diagnostics/log';
import * as settings from '../../engine/settings/resolve';
import { sprayLengthOf } from '../../engine/areaShapes';
import { tableDefaultsFrom } from '../../engine/settings/registry';
import useSteadyProps from '../../hooks/useSteadyProps';
import BattlefieldPanel from '../BattlefieldPanel.jsx';

/**
 * The map behind a memo, its props steadied (useSteadyProps): the shell re-renders on every presence heartbeat with
 * its handlers made anew, and the whole map used to redraw with it (the perf overhaul, Stage 1).
 */
const SteadyPanel = memo(BattlefieldPanel);
const STEADY = { relay: ['readSceneSet'] };

/** The table's movement rulings, as the map reads them. */
function movementRules(permissions) {
  return {
    fatigueSlowsMovement: settings.resolveFatigueSlowsMovement(permissions),
    climbCosts: settings.resolveClimbCostsMovement(permissions),
    climbTraining: settings.resolveClimbTrainingSetsRate(permissions),
    levelPickerSpends: settings.resolveLevelPickerSpendsMovement(permissions),
  };
}

/** The props derived here rather than passed through. */
function derivedProps(map, { activeRoom, canSeeRooms, setReplaceDialog, updateCombatantSheet, armed }) {
  const { roster, targetId, sessionId, permissions } = map;
  return {
    activeRoomId: activeRoom?.id ?? null,
    onReplacePick: canSeeRooms ? (outgoingId) => setReplaceDialog({ outgoingId }) : undefined,
    onStampTeam: (id, team) => updateCombatantSheet(id, (sheet) => ({ ...sheet, team })),
    onRedeployOnMapLoad: (v) =>
      warnIfRejected(
        multiplayer.writeHouseRule(sessionId, permissions, 'redeployOnMapLoad', v),
        'settings',
        'redeploy-on-map-load write',
      ),
    targetMovement: roster.find((c) => c.id === targetId)?.sheet?.movement,
    weaponRange: armed?.range,
    weaponRangeM: sprayLengthOf(armed),
    // Where the display's options start for a viewer who has chosen nothing (plan map-display, C).
    tableDefaults: tableDefaultsFrom(permissions?.houseRules),
    ...movementRules(permissions),
  };
}

/**
 * @param {object} props
 * @param {object} props.map - the BattlefieldPanel props passed through, plus
 *   `sessionId` and `permissions` (read here, not passed on)
 * @param {object} props.derive - activeRoom, canSeeRooms, setReplaceDialog,
 *   updateCombatantSheet, armed (the armed plate)
 */
export default function ShellBattlefield({ map, derive }) {
  const { sessionId: _sessionId, permissions: _permissions, ...passed } = map;
  // `readSceneSet` is read from an effect (a wings card's thumbnail), and made anew each render: an action to relay.
  const props = useSteadyProps({ ...passed, ...derivedProps(map, derive) }, STEADY);
  return <SteadyPanel {...props} />;
}
